import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, PhoneCall } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError, WHATSAPP_LINK } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const WHENS = ["Now (within an hour)", "Today", "This week"];

/**
 * Compact 3-field hero widget: name, phone, when-to-call.
 * On submit: creates a lead (source=reserve) and opens WhatsApp with prefilled context.
 */
export default function ReserveSlot() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [when, setWhen] = useState(WHENS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !phone) return toast.error("Please enter your name and phone.");
    setSubmitting(true);
    try {
      await api.post("/reserve", {
        name, phone, email: `reserve+${phone.replace(/[^0-9]/g,'')}@drtegbirvascular.com`, source: "reserve", call_when: when, consent: true,
      });
      trackEvent("reserve_slot_submitted", { call_when: when });
      setDone(true);
      const msg = `Hello Dr Sidhu, I just reserved a call-back on the website. Name: ${name}. Please call: ${when}.`;
      window.open(WHATSAPP_LINK(msg), "_blank", "noopener");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid="reserve-success"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="hairline bg-white/70 backdrop-blur p-5 md:p-6"
      >
        <div className="kbd text-forest">— We'll call you</div>
        <p className="mt-2 text-sm md:text-base text-ink leading-snug">
          Thanks, <span className="font-medium">{name.split(" ")[0]}</span>. The team will reach out <span className="font-medium">{when.toLowerCase()}</span>. If WhatsApp didn't open, message us directly.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.form
      data-testid="reserve-widget"
      onSubmit={submit}
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="hairline bg-white/70 backdrop-blur p-4 md:p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="kbd inline-flex items-center gap-2"><PhoneCall size={12} className="text-crimson"/> Reserve a call-back</div>
        <div className="hidden sm:block kbd text-ink-muted">No account. 30 seconds.</div>
      </div>
      <div className="grid grid-cols-12 gap-2 md:gap-3">
        <div className="col-span-12 md:col-span-4">
          <Input data-testid="reserve-name" required placeholder="Your name" value={name} onChange={(e)=>setName(e.target.value)} className="h-11 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink px-0"/>
        </div>
        <div className="col-span-12 md:col-span-4">
          <Input data-testid="reserve-phone" required placeholder="Phone / WhatsApp" value={phone} onChange={(e)=>setPhone(e.target.value)} className="h-11 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink px-0"/>
        </div>
        <div className="col-span-8 md:col-span-3">
          <Select value={when} onValueChange={setWhen}>
            <SelectTrigger data-testid="reserve-when" className="h-11 rounded-none border-0 border-b border-ink/30 bg-transparent focus:ring-0 px-0 text-sm"><SelectValue/></SelectTrigger>
            <SelectContent>{WHENS.map((w)=><SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-4 md:col-span-1 flex items-end">
          <button data-testid="reserve-submit" type="submit" disabled={submitting} className="btn-pill-inv w-full justify-center h-11 !py-0 !px-3 disabled:opacity-60" aria-label="Reserve">
            {submitting ? "…" : <ArrowUpRight size={16}/>}
          </button>
        </div>
      </div>
    </motion.form>
  );
}
